import React, { useEffect } from 'react'
import { useStyle } from '../contexts/StyleContext'
import { getBackgroundCSS, getBackgroundImageUrl } from '../utils/styleUtils'

interface PageStyleProviderProps {
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
}

/**
 * Page-specific style provider that applies background styles
 * and other page-level styling from the merged style context
 */
export const PageStyleProvider: React.FC<PageStyleProviderProps> = ({
  children,
  className = '',
  style: additionalStyle = {},
}) => {
  const { style } = useStyle()

  // Apply background styles to document body
  useEffect(() => {
    if (!style) return

    const backgroundCSS = getBackgroundCSS(style)
    
    // Debug logging
    console.log('PageStyleProvider: Applying background styles', {
      style,
      backgroundCSS,
      backgroundImageUrl: getBackgroundImageUrl(style)
    })
    
    // Apply each CSS property to the body
    Object.entries(backgroundCSS).forEach(([property, value]) => {
      document.body.style[property as any] = value as string
    })
    
    // Ensure body takes full viewport height
    document.body.style.minHeight = '100vh'
    document.body.style.margin = '0'
    document.body.style.padding = '0'
    
    // If there's a background image, ensure it's fixed
    if (backgroundCSS.backgroundImage) {
      document.body.style.backgroundAttachment = 'fixed'
    }

    // Cleanup function to remove styles
    return () => {
      Object.keys(backgroundCSS).forEach(property => {
        document.body.style[property as any] = ''
      })
      document.body.style.minHeight = ''
      document.body.style.margin = ''
      document.body.style.padding = ''
      document.body.style.backgroundAttachment = ''
    }
  }, [style])

  // Handle favicon updates
  useEffect(() => {
    if (!style) return

    const updateFavicon = () => {
      // Remove existing favicon links
      const existingFavicons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]')
      existingFavicons.forEach(el => el.remove())

      let faviconUrl: string | null = null

      if (style.faviconUrl) {
        faviconUrl = style.faviconUrl
      } else if (style.faviconRemoteUrl) {
        faviconUrl = `/legacy/${style.faviconRemoteUrl}`
      }

      if (faviconUrl) {
        const link = document.createElement('link')
        link.rel = 'icon'
        link.type = 'image/x-icon'
        link.href = faviconUrl
        document.head.appendChild(link)

        // Also add as shortcut icon for older browsers
        const shortcutLink = document.createElement('link')
        shortcutLink.rel = 'shortcut icon'
        shortcutLink.type = 'image/x-icon'
        shortcutLink.href = faviconUrl
        document.head.appendChild(shortcutLink)
      }
    }

    updateFavicon()

    // No cleanup needed as next style change will remove old favicons
  }, [style?.faviconUrl, style?.faviconRemoteUrl])

  // Handle meta theme color for mobile browsers
  useEffect(() => {
    if (!style?.headerColor) return

    let metaThemeColor = document.querySelector('meta[name="theme-color"]')
    
    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta')
      metaThemeColor.setAttribute('name', 'theme-color')
      document.head.appendChild(metaThemeColor)
    }
    
    metaThemeColor.setAttribute('content', style.headerColor)

    return () => {
      if (metaThemeColor?.parentNode) {
        metaThemeColor.parentNode.removeChild(metaThemeColor)
      }
    }
  }, [style?.headerColor])

  // Preload background image if present
  useEffect(() => {
    const backgroundUrl = getBackgroundImageUrl(style)
    if (!backgroundUrl) return

    const link = document.createElement('link')
    link.rel = 'preload'
    link.as = 'image'
    link.href = backgroundUrl
    document.head.appendChild(link)

    return () => {
      if (link.parentNode) {
        link.parentNode.removeChild(link)
      }
    }
  }, [style])

  return (
    <div 
      className={`page-style-container ${className}`.trim()}
      style={additionalStyle}
    >
      {children}
    </div>
  )
}